export type PacketType = 'HP Appeal' | 'QIO Appeal';
export type AppealType = 'Expedited' | 'Standard';
export type ActionType = 'End all covered services (NOMNC/DENC)' | 'Reduce covered services' | 'Deny admission to Post-Acute care' | 'Other';
export type PrimaryDocumentReviewed = 'Clinical Review Note' | 'Physician Review Note' | 'N/A';

export enum PACKET_TYPES{
  HPAppeal = 'HP Appeal',
  QIOAppeal = 'QIO Appeal'
}

export interface AppealsPacketFormData {
  // Common fields
  packetType: PacketType | null;
  lastApprovedDay: Date | null;
  actionTypes: ActionType[];
  actionTypeOther: string;
  appealType: AppealType;
  primaryHealthPlanContact: string;
  
  // QIO specific fields
  signedNomnc: string | null;
  additionalNotesOnNomnc: string;
  additionalNotesOnNomncOther: string;
  carelonFollowUpDocumented: boolean | null;
  carelonFollowUpDate: Date | null;
  
  // Exhibits fields (for both Table of Contents and Exhibits)
  exhibits: string[];
  caseManagementNotesDetails: string;
  
  // UM Determination Summary fields
  actionBeingAppealed: string[];
  actionBeingAppealedOther: string;
  caseSnapshot: string;
  historyOfPresentIllness: string; 
  primaryDenialReasons: string[]; 
  supportingFact: string; 
  criteriaUsed: string[];
  criteriaUsedOther: string;
  primaryRationale: string;
  primaryDocumentReviewed: PrimaryDocumentReviewed | null;
  primaryDocumentsReviewedText: string;
  
  // Transition of Care Coordination fields
  carelonCoordinationActions: CarelonCoordinationAction[];
  carelonCoordinationNotes: string;
  carelonCoordinationOtherDetails: string;
  
  // Chronology of Events fields
  outreachRfiDates: Date[];
  
  // Denial to Evidence Crosswalk fields
  transitionOfCareCoordination: string;
  
  // Selected documents
  selectedDocumentIds: string[];
  selectedDocuments?: SelectedDocumentInfo[];
  documentFileName?: string;
}

export interface SelectedDocumentInfo {
  docMetaDataId: string;
  documentSource: string;
  documentFileName?: string;
}

export interface NoteTypesConfig {
  noteTypes: string[];
  referenceId?: string | number;
}

export interface Note {
  noteId?: string;
  tenantId?: string;
  tenantName?: string;
  noteTypeId?: string;
  noteType?: string;
  noteCategoryId?: string;
  noteCategoryName?: string;
  referenceClue?: string;
  noteComment?: string;
  memberId?: string;
  serviceLineId?: string;
  authorizationId?: string;
  comment?: string;
  createdOn?: Date | string;
  createdBy?: string;
  modifiedOn?: Date | string;
  modifiedBy?: string;
  physicianComment?: string;
  noteDate?: string;
  [key: string]: any; 
}

export interface ServiceLineSummary {
  serviceLineId?: string;
  authorizationDetailId: string;
  sequenceNumber: number;
  serviceStatus: string;
  serviceStatusReason?: string;
  skillCode?: string;
  reviewPeriod?: string;
  numberOfRequestedVisits?: number;
  discipline?: string;
  dateTimeRequestReceived?: string;
  requestType?: string;
  dateTimeFaxReceived?: string;
}

export interface PrimaryDenialReason {
  reason: string;
  supportingFact: string;
}

export interface CarelonCoordinationAction {
  action: string;
  date: Date | null;
}

export interface AuthAppealDetails {
  appealAndGrievanceId?: string;
  admitDate: string;
  memberName: string;
  memberId: string;
  mbi: string;
  dateOfBirth: string;
  healthPlan: string;
  lineOfBusiness: string;
  authorizationNumber: string;
  authorizationType: string;
  setting: string;
  facilityType: {
    description: string;
  };
  facilityName: string;
  agReferenceNumber: string;
  admissionDate: string;
  appealFiledDate: string;
  determinationLetterIssuedDate: string;
  memberData?: any;
  
  // QIO specific
  caseAppealNumber?: string;
  qioNotificationReceivedDate?: string;
  nomncDeliveredToServiceProviderDate?: string;
  dencDeliveredDate?: string;
  nomncDeliverySignatureDate?: string;
}


export const ACTION_TYPE_OPTIONS = [
  'End all covered services (NOMNC/DENC)',
  'Reduce covered services',
  'Deny admission to Post-Acute care',
  'Other'
];

export const ADDITIONAL_NOTES_NOMNC_OPTIONS = [
  'Awaiting facility/provider to return',
  'Invalid NOMNC returned',
  'Sent by facility/provider directly to QIO',
  'Member declined to sign',
  'Other'
];

export const CRITERIA_USED_OPTIONS = [
  'Medicare Benefit Policy Manual (MBPM), Chapter 8 – SNF Level of Care',
  'Medicare Benefit Policy Manual (MBPM), Chapter 1 – IRF Level of Care',
  'Milliman Care Guidelines (MCG), GRG-049',
  'Milliman Care Guidelines (MCG), GRG-050',
  'Other'
];

export const ACTION_BEING_APPEALED_OPTIONS = [
  'Terminate ALL covered services',
  'Reduce/partial terminate',
  'Deny admission',
  'Other'
];

export const CARELON_COORDINATION_ACTIONS = [
  'Facility discharge planning team notified of adverse determination and LAD',
  'Case management referral/escalation initiated (if applicable)',
  'Outreach/RFI sent to facility for incomplete or insufficient discharge planning documentation (if applicable)',
  'No additional discharge planning detail was provided before final determination',
  'Other'
];

export const DEFAULT_TRANSITION_TEXT = 'While continued post-acute facility based care is not medically necessary, transition of care needs are recognized. Appropriate notification and escalation to facility discharge planning and case management were initiated. Discharge planning and execution remain the responsibility of the facility and care management teams.';

export const MAX_FILE_SIZE_QIO = 150;
export const MAX_FILE_SIZE_HP = 25;
